import * as CubiqTS from "shared/cubiq-model-multi-asset";
import * as CubiqServices from "shared/cubiq-services";
import { BfxPopupNotificationData } from "./bfx-popup-notification-data.model";
import { BfxPopupNotificationCommand } from "./bfx-popup-notification-command.enum";
import {
  BfxBlotterOrdersContextMenuItemAcceptanceState,
  IOrderNotifData,
} from "shared/cubiq-services";
import { BFXNotificationType } from "../../../shared/cubiq-services/model/core/notifications/BFXNotificationType";
import { BFXNotificationSubType } from "../../../shared/cubiq-services/model/core/notifications/BFXNotificationSubType";

export class BfxPopupAcceptanceNewNotificationData extends BfxPopupNotificationData {
  public acceptanceState: BfxBlotterOrdersContextMenuItemAcceptanceState;
  /**
   * Flag used to determine the action and action types available
   */
  private _bmarkOrder: boolean = false;

  public isAutoHedged: boolean = false;

  constructor(
    type: BFXNotificationType,
    subType: BFXNotificationSubType,
    private activeUser: CubiqServices.ActiveUser,
    private hasAccept: boolean,
    private hasReject: boolean,
    private hasUnrestricted: boolean,
  ) {
    super(type, "icon-bfx_new", subType);
    this.acceptanceState = new BfxBlotterOrdersContextMenuItemAcceptanceState();
    this.acceptanceState.enabled = true;
    this.acceptanceState.callOrderRateSide = undefined;
    this.acceptanceState.queryCallOrderRateSide = undefined;
    this.acceptanceState.actionRequests = {
      accept: {
        acceptance: new Map<CubiqTS.AcceptanceType, string[]>(),
        xfer: new Map<string, string[]>(),
      },
      acceptInto: {
        acceptance: new Map<CubiqTS.AcceptanceType, string[]>(),
        xfer: new Map<string, string[]>(),
      },
      reject: {
        acceptance: new Map<CubiqTS.AcceptanceType, string[]>(),
        xfer: new Map<string, string[]>(),
      },
    };
  }

  reInitMaps() {
    this.acceptanceState.actionRequests.accept.acceptance.clear();
    this.acceptanceState.actionRequests.accept.xfer.clear();
    this.acceptanceState.actionRequests.acceptInto.acceptance.clear();
    this.acceptanceState.actionRequests.acceptInto.xfer.clear();
    this.acceptanceState.actionRequests.reject.acceptance.clear();
    this.acceptanceState.actionRequests.reject.xfer.clear();
  }

  private addToAcceptMap(orderId: string) {
    if (!this.acceptanceState.actionRequests.accept.acceptance.has(CubiqTS.AcceptanceType.ENTRY)) {
      this.acceptanceState.actionRequests.accept.acceptance.set(CubiqTS.AcceptanceType.ENTRY, []);
    }
    this.acceptanceState.actionRequests.accept.acceptance
      .get(CubiqTS.AcceptanceType.ENTRY)
      .push(orderId);
  }
  private addToAcceptIntoMap(orderId: string) {
    if (
      !this.acceptanceState.actionRequests.acceptInto.acceptance.has(CubiqTS.AcceptanceType.ENTRY)
    ) {
      this.acceptanceState.actionRequests.acceptInto.acceptance.set(
        CubiqTS.AcceptanceType.ENTRY,
        [],
      );
    }
    this.acceptanceState.actionRequests.acceptInto.acceptance
      .get(CubiqTS.AcceptanceType.ENTRY)
      .push(orderId);
  }
  private addToRejectMap(orderId: string) {
    if (!this.acceptanceState.actionRequests.reject.acceptance.has(CubiqTS.AcceptanceType.ENTRY)) {
      this.acceptanceState.actionRequests.reject.acceptance.set(CubiqTS.AcceptanceType.ENTRY, []);
    }
    this.acceptanceState.actionRequests.reject.acceptance
      .get(CubiqTS.AcceptanceType.ENTRY)
      .push(orderId);
  }

  protected updateAllowableActions() {
    this.actions = [];
    this.reInitMaps();
    if (
      this.hasUnrestricted ||
      (this.activeUser.userTypeId === CubiqTS.UserType.TRADER && (this.hasAccept || this.hasReject))
    ) {
      let acceptCount: number = 0;
      let acceptIntoCount: number = 0;
      let acceptTopDownCount: number = 0;
      let rejectCount: number = 0;
      let queryCallOrderRateSideCount: number = 0;

      this.payload.forEach((item: IOrderNotifData) => {
        let requiresCallOrderRateSide =
          CubiqTS.RequestOrderType.CALL === item.requestedOrderType &&
          item.hasTrackingSide == false;

        if (item.deskId !== "-1") {
          //default trader group, needs accept into
          if (this.hasUnrestricted || this.activeUser.tradingGroupsIds.includes(item.deskId)) {
            if (this.hasUnrestricted || this.hasAccept) {
              if (requiresCallOrderRateSide) {
                acceptTopDownCount++;
                this.addToAcceptMap(item.orderId);
              } else {
                acceptCount++;
                this.addToAcceptMap(item.orderId);
              }
              // For BMarks, we do not currently support accept Into
              if (this.activeUser.userTypeId === CubiqTS.UserType.TRADER && !this._bmarkOrder) {
                acceptIntoCount++;
                this.addToAcceptIntoMap(item.orderId);

                if (requiresCallOrderRateSide) {
                  queryCallOrderRateSideCount++;
                }
              }
            }
            if (this.hasUnrestricted || this.hasReject) {
              this.addToRejectMap(item.orderId);
              rejectCount++;
            }
          }
        } else {
          // For BMarks, we do not currently support accept Into
          if (
            (this.hasUnrestricted || this.hasAccept) &&
            this.activeUser.userTypeId === CubiqTS.UserType.TRADER &&
            !this._bmarkOrder
          ) {
            acceptIntoCount++;
            this.addToAcceptIntoMap(item.orderId);

            if (requiresCallOrderRateSide) {
              queryCallOrderRateSideCount++;
            }
          }
          if (this.hasUnrestricted || this.hasReject) {
            this.addToRejectMap(item.orderId);
            rejectCount++;
          }
        }
      });

      if (acceptCount == this.payload.length) {
        const commandId = this._bmarkOrder
          ? BfxPopupNotificationCommand.POPUP_INTERVENTION_TRADER_ACCEPT
          : BfxPopupNotificationCommand.POPUP_DI_ACCEPT;
        this.actions.push({
          label: "Accept",
          command: commandId,
          icon: "",
          styleClass: "bfx-context-menu-item-enabled",
        });
      }
      if (acceptTopDownCount == this.payload.length) {
        this.actions.push({
          label: "Accept Top",
          command: BfxPopupNotificationCommand.POPUP_DI_ACCEPT_TOPSIDE,
          icon: "",
          styleClass: "bfx-context-menu-item-enabled",
        });
        this.actions.push({
          label: "Accept Down",
          command: BfxPopupNotificationCommand.POPUP_DI_ACCEPT_DOWNSIDE,
          icon: "",
          styleClass: "bfx-context-menu-item-enabled",
        });
      }
      if (
        acceptIntoCount == this.payload.length &&
        (queryCallOrderRateSideCount == 0 || acceptIntoCount == queryCallOrderRateSideCount)
      ) {
        this.acceptanceState.queryCallOrderRateSide =
          acceptIntoCount == queryCallOrderRateSideCount;
        this.actions.push({
          label: "Accept Into",
          command: BfxPopupNotificationCommand.POPUP_DI_ACCEPT_INTO,
          icon: "",
          styleClass: "bfx-context-menu-item-enabled",
        });
      }
      if (rejectCount == this.payload.length) {
        const commandId = this._bmarkOrder
          ? BfxPopupNotificationCommand.POPUP_INTERVENTION_TRADER_REJECT
          : BfxPopupNotificationCommand.POPUP_DI_REJECT;
        this.actions.push({
          label: "Reject",
          command: commandId,
          icon: "fa icon-menu-error bfx-context-menu-icon",
          styleClass: "bfx-context-menu-item-enabled",
          separatorBefore: this.actions.length > 0,
        });
      }
    }
  }

  destroy() {
    super.destroy();
    this.activeUser = null;
  }

  /**
   * Update bmarkOrder flag and ensure the typeStr of the data is keyed off the
   * bmarkOrder flag
   */
  public set isBmarkOrder(value: boolean) {
    if (value != this._bmarkOrder) {
      this._bmarkOrder = value;
      if (value) {
        // Ensure that bmark and non-bmark acceptance notification do not get merged
        if (!this.typeStr.endsWith("_BMARK")) {
          this.typeStr = this.typeStr + "_BMARK";
        }
      } else {
        if (this.typeStr.endsWith("_BMARK")) {
          // Remove the prefix
          this.typeStr = this.typeStr.substring(0, this.typeStr.length - 6);
        }
      }
      this.updateAllowableActions();
    }
  }

  public get isBmarkOrder(): boolean {
    return this._bmarkOrder;
  }
}
